import { Link } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';

const STATUS_CONFIG = {
  running:     { key: 'bgj_st_running',     color: '#1d4ed8', bg: '#dbeafe' },
  done:        { key: 'bgj_st_done',        color: '#15803d', bg: '#dcfce7' },
  error:       { key: 'bgj_st_error',       color: '#b91c1c', bg: '#fee2e2' },
  stopped:     { key: 'bgj_st_stopped',     color: '#6b7280', bg: '#f1f5f9' },
  interrupted: { key: 'bgj_st_interrupted', color: '#c2410c', bg: '#ffedd5' },
};

function fmt(dt) {
  return dt ? new Date(dt).toLocaleString() : '—';
}

export default function BackgroundJobsPage() {
  const { t } = useLang();
  const { jobs, refresh } = useBackgroundJobs();

  async function handleStop(id) {
    try { await api.post(`/api/background-jobs/${id}/stop`); } catch { /* */ }
    refresh();
  }

  async function handleDelete(id) {
    if (!window.confirm(t('bgj_confirm_delete'))) return;
    try { await api.delete(`/api/background-jobs/${id}`); } catch { /* */ }
    refresh();
  }

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <h2>{t('bgj_title')}</h2>
          <div className="text-muted small">{t('bgj_subtitle')}</div>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={refresh}>
          <i className="bi bi-arrow-clockwise me-1" />{t('refresh')}
        </button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-muted text-center py-5">{t('bgj_none')}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>{t('bgj_col_status')}</th>
                <th>{t('bgj_col_audit')}</th>
                <th style={{ minWidth: 180 }}>{t('bgj_col_progress')}</th>
                <th className="text-center">{t('bgj_col_processed')}</th>
                <th className="text-center">{t('bgj_col_errors')}</th>
                <th>{t('bgj_col_started')}</th>
                <th>{t('bgj_col_finished')}</th>
                <th className="text-end">{t('bgj_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.stopped;
                const pct = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;
                return (
                  <tr key={job.id}>
                    <td>
                      <span className="badge" style={{ background: cfg.bg, color: cfg.color, fontWeight: 600 }}>
                        {job.status === 'running' && (
                          <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }} />
                        )}
                        {t(cfg.key)}
                      </span>
                    </td>
                    <td>
                      {job.audit_id
                        ? <Link to={`/audits/${job.audit_id}`} className="text-decoration-none">
                            {job.audit_name || `#${job.audit_id}`}
                          </Link>
                        : '—'}
                      {job.client_name && <div className="text-muted small">{job.client_name}</div>}
                    </td>
                    <td>
                      <div className="d-flex justify-content-between small text-muted mb-1">
                        <span className="text-truncate" style={{ maxWidth: 130 }}>{job.current_label || '—'}</span>
                        <span>{job.done}/{job.total}</span>
                      </div>
                      <div className="progress" style={{ height: 5 }}>
                        <div className="progress-bar" style={{ width: `${pct}%`, background: cfg.color }} />
                      </div>
                    </td>
                    <td className="text-center">{job.polished}</td>
                    <td className="text-center">
                      {job.errors > 0 ? <span className="text-danger fw-semibold">{job.errors}</span> : '0'}
                    </td>
                    <td className="small text-muted">{fmt(job.started_at)}</td>
                    <td className="small text-muted">{fmt(job.finished_at)}</td>
                    <td className="text-end">
                      {job.status === 'running' && (
                        <button className="btn btn-sm btn-outline-warning me-1" onClick={() => handleStop(job.id)}>
                          <i className="bi bi-stop-circle me-1" />{t('bgj_stop')}
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(job.id)}
                        disabled={job.status === 'running'} title={job.status === 'running' ? t('bgj_stop') : t('bgj_delete')}>
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
